import React, { memo } from 'react';
import { Handle, Position } from '@xyflow/react';
import type { NodeProps } from '@xyflow/react';
import type { OrgNodeData } from './orgChartUtils';

const nodeStyles: Record<string, React.CSSProperties> = {
  base: {
    padding: '10px 16px',
    borderRadius: 8,
    minWidth: 160,
    maxWidth: 200,
    textAlign: 'center',
    cursor: 'pointer',
    fontFamily: 'JF-Flat-Regular, system-ui, sans-serif',
    fontSize: 13,
    position: 'relative',
    transition: 'box-shadow 0.2s',
  },
};

function OrgChartNodeComponent({ data }: NodeProps) {
  const nodeData = data as unknown as OrgNodeData;
  const {
    treeNode,
    label,
    level,
    isExpanded,
    childrenCount,
    primaryColor,
    secondaryColor,
    onToggleExpand,
    onNodeClick,
    inScope = true,
    isAnchor = true,
  } = nodeData;

  const isSyntheticRoot = treeNode.id === -1;
  const goalsCount = treeNode.strategic_goals?.length ?? 0;

  // In-scope nodes are clickable. The anchor (the user's own department, e.g.
  // قسم الدراسات والاستراتيجية) stays GREEN; the rest of the in-scope tree
  // renders RED. Out-of-scope nodes render in muted gray and ignore clicks.
  let style: React.CSSProperties;
  if (inScope && !isSyntheticRoot) {
    style = {
      ...nodeStyles.base,
      backgroundColor: isAnchor ? '#22C55E' : '#EF4444',
      color: '#FFFFFF',
      border: isAnchor ? '2px solid #16A34A' : '2px solid #DC2626',
      fontFamily: 'JF-Flat-Medium, system-ui, sans-serif',
      fontWeight: 600,
      cursor: 'pointer',
    };
  } else if (isSyntheticRoot || level === 0) {
    // Root org node (only rendered when not in-scope, e.g., for a section
    // head where the DHR root is just visual context)
    style = {
      ...nodeStyles.base,
      backgroundColor: secondaryColor,
      color: '#FFFFFF',
      fontFamily: 'JF-Flat-Medium, system-ui, sans-serif',
      fontSize: 14,
      fontWeight: 600,
      cursor: 'default',
      opacity: 0.7,
    };
  } else if (level === 1) {
    // Out-of-scope directorate
    style = {
      ...nodeStyles.base,
      backgroundColor: '#E5E7EB',
      color: '#6B7280',
      border: '1.5px solid #D1D5DB',
      fontFamily: 'JF-Flat-Medium, system-ui, sans-serif',
      cursor: 'default',
      opacity: 0.7,
    };
  } else if (level === 2) {
    // Out-of-scope section
    style = {
      ...nodeStyles.base,
      backgroundColor: '#F3F4F6',
      border: '1.5px solid #D1D5DB',
      color: '#6B7280',
      cursor: 'default',
      opacity: 0.7,
    };
  } else {
    // Out-of-scope subsidiary
    style = {
      ...nodeStyles.base,
      backgroundColor: '#F9FAFB',
      border: '1.5px dashed #D1D5DB',
      color: '#9CA3AF',
      fontSize: 12,
      cursor: 'default',
      opacity: 0.6,
    };
  }

  const handleClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (isSyntheticRoot || !inScope) return;
    onNodeClick(treeNode);
  };

  const handleToggle = (e: React.MouseEvent) => {
    e.stopPropagation();
    onToggleExpand(treeNode.id);
  };

  return (
    <div
      style={style}
      onClick={handleClick}
      onMouseEnter={(e) => {
        (e.currentTarget as HTMLDivElement).style.boxShadow = '0 4px 12px rgba(0,0,0,0.15)';
      }}
      onMouseLeave={(e) => {
        (e.currentTarget as HTMLDivElement).style.boxShadow = 'none';
      }}
    >
      {/* Target handle (right) — incoming edge from parent, which sits to
          the right in the RL layout */}
      {!isSyntheticRoot && (
        <Handle
          type="target"
          position={Position.Right}
          style={{ opacity: 0, width: 8, height: 8 }}
        />
      )}

      {/* Node label */}
      <div style={{ marginBottom: childrenCount > 0 || goalsCount > 0 ? 4 : 0 }}>
        {label}
      </div>

      {/* Goals badge */}
      {goalsCount > 0 && (
        <div
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 4,
            backgroundColor: 'rgba(139, 92, 246, 0.15)',
            color: '#7C3AED',
            borderRadius: 10,
            padding: '2px 8px',
            fontSize: 11,
          }}
        >
          <span style={{ fontSize: 10 }}>🎯</span>
          {goalsCount}
        </div>
      )}

      {/* Expand/collapse toggle — anchored on the LEFT for the RL layout,
          since that's the side where children appear. */}
      {childrenCount > 0 && (
        <div
          onClick={handleToggle}
          style={{
            position: 'absolute',
            top: '50%',
            left: -12,
            transform: 'translateY(-50%)',
            width: 20,
            height: 20,
            borderRadius: '50%',
            backgroundColor: '#FFFFFF',
            border: '1.5px solid #D1D5DB',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            fontSize: 10,
            color: '#6B7280',
            zIndex: 10,
          }}
        >
          {isExpanded ? '◀' : '▶'}
        </div>
      )}

      {/* Source handle (left) — children fan out to the left in RL layout */}
      <Handle
        type="source"
        position={Position.Left}
        style={{ opacity: 0, width: 8, height: 8 }}
      />
    </div>
  );
}

export default memo(OrgChartNodeComponent);
